/**
 * **멤버십 목록의 각 행**에 대한 판정 — 순수 함수.
 *
 * `usePermissions()`는 `currentMembership` 하나만 답한다. 그런데 탈퇴 모달·현장 이관·조직 셀렉터는
 * 「내가 속한 **다른** 조직들 중 어디가 해당되나」를 묻는다 — 훅이 원리적으로 답할 수 없는 질문이라
 * 목록의 행마다 같은 규칙을 적용하는 순수 함수가 필요하다. `/auth/me`가 멤버십 **전부**에
 * `isOwner`와 `permissions`를 싣는다(`auth.service.ts`의 `getMe` → `resolvePermissions`).
 *
 * ## 두 축을 섞지 않는다 (company-rbac P6-b)
 *
 * | | 묻는 것 | 판정 |
 * |---|---|---|
 * | 소유자 **성질** | 이 조직이 **내 것인가** | `isOwner` 필드 |
 * | 권한 **key** | 이 조직에서 이 **행위**를 해도 되나 | `permissions`에 그 key가 있나 |
 *
 * 규약은 `hooks/usePermissions.ts` doc와 같다 — 「`isOwner`는 권한 key로 못 푸는 판정에만 쓴다.
 * 『이 행위를 해도 되나』는 key」. 서버가 소유자에게 **전체 키**를 주므로
 * (`permissions.service.ts` `resolvePermissions`: `if (isOwner) return { permissions: [...PERMISSION_KEYS] }`)
 * key 보유자 ⊇ 소유자다 — key로 묻는 것은 소유자를 **이미 포함**하고, `isOwner ||`를 덧붙이면
 * 중복이자 규약 위반이다.
 *
 * ## 어느 쪽인지는 **서버 게이트가 정한다**
 *
 * 화면이 서버보다 좁으면 「서버는 허용하는데 목록에 안 뜨는」 자리가 생기고, 넓으면 막다른 길이
 * 된다. 그래서 자리마다 서버의 최종 게이트를 찾아 같은 축으로 맞춘다 — 각 함수 doc에 그 게이트의
 * 위치를 적어 둔다.
 *
 * 옛 `role === 'OWNER'` 비교는 어느 쪽에서도 쓰지 않는다. 두 값은 독립이라 양방향으로 갈린다:
 * 커스텀 소유자 역할(`isOwner: true`, `role ≠ 'OWNER'`)은 옛 축에서 **빠지고**, 역할 배정을 받았지만
 * 소유자 역할이 없는 채 옛 컬럼만 'OWNER'인 멤버는 옛 축에서 **부당하게 들어온다**.
 * (옛 role 컬럼은 **라벨·초대 축**에서만 정본이다 — `lib/organizations.ts`의 `isOwner` doc, P7까지.)
 */

import type { PermissionKey } from '@safetyhub/shared';

/** 이 판정들에 필요한 멤버십 조각만. 옛 `role`은 **읽지 않는다**(위 doc). */
export interface MembershipAccessRow {
  /**
   * 소유자 **성질** — 서버 `resolvePermissions().isOwner`. 옛 `role` 컬럼과 독립이다
   * (소유자 역할 «배정»에서 온다).
   */
  isOwner?: boolean;
  /**
   * 그 조직에서 내가 가진 권한 key 합집합 — 서버 `resolvePermissions().permissions`.
   * 소유자면 전체 키가 실린다.
   */
  permissions?: string[];
  /** `deletedAt`이 있으면 soft delete(삭제 예정) 상태. */
  company: { deletedAt?: string | null };
}

/**
 * 이 멤버십이 그 조직에서 해당 권한 key를 갖는가 — `usePermissions().can(key)`의 목록판.
 *
 * ⚠️ `permissions` 부재(`undefined`)를 **false로 떨어뜨린다.** 옛 응답 형상이나 로딩 중 스냅샷이
 * 섞여도 `undefined.includes`로 죽지 않아야 하고, 「모르면 허용」은 게이트로 쓰면 안 되는 기본값이다
 * (판정 정본은 서버이므로 화면이 조금 좁은 것은 안전하지만 그 반대는 아니다).
 */
export function hasMembershipPermission(
  membership: MembershipAccessRow | null | undefined,
  key: PermissionKey,
): boolean {
  const permissions = membership?.permissions;
  return Array.isArray(permissions) && permissions.includes(key);
}

/**
 * 내가 **소유자**이고, 삭제 예정이 아닌 조직인가 — 소유자 **성질** 축.
 *
 * 소비처는 탈퇴 모달(`DeleteAccountModal`)의 「내가 소유자라 폐쇄될 조직」 목록이다. 짝이 되는
 * `soleOwnerOrganizations`를 서버가 같은 성질 축으로 뽑으므로(유일 소유자 판정·마지막 소유자 보호)
 * 여기서 축이 갈리면 두 목록의 diff(`nonSoleOwnerOrgs`)가 어긋난다. 권한 key로 표현되지 않는
 * 판정이라 성질이 정본이다.
 *
 * 조직 유형(`company.category`)은 축이 **아니다** — `WORKSPACE`("개인/팀 작업공간")도 멤버가
 * 여럿일 수 있고, 폐쇄되면 그 멤버들의 접근이 함께 끊긴다.
 */
export function isOwnedActiveOrganization(
  membership: MembershipAccessRow | null | undefined,
): boolean {
  if (!membership) return false;
  return membership.isOwner === true && !membership.company.deletedAt;
}

/**
 * 이 조직으로 현장을 이관해 올 수 있나 — 권한 **key** 축(`site.transfer`).
 *
 * 서버의 최종 게이트가 성질이 아니라 key다 — `sites/site-transfer.service.ts`의 `assertCanTransfer`가
 * `hasPermission(companyId, memberId, 'site.transfer')`를 보고, `requestTransfer`·
 * `transferToMyOrganization`(보내는 조직·받는 조직 **양쪽**)·`acceptTransfer`가 전부 그것을 부른다.
 * 소유자 성질로 좁히면 시드 역할 `overall-safety-health-manager`·`safety-health-manager-in-charge`가
 * **서버는 허용하는데 목록에 안 뜨는** 조직을 갖게 된다(화면이 서버보다 좁은 거짓말).
 *
 * 삭제 예정 조직은 제외한다 — 폐쇄 대기 중인 조직으로 현장을 옮기는 것은 목적지가 사라지는 길이다.
 */
export function canReceiveSiteTransfer(
  membership: MembershipAccessRow | null | undefined,
): boolean {
  if (!membership) return false;
  return !membership.company.deletedAt && hasMembershipPermission(membership, 'site.transfer');
}
